import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  CircularProgress,
  Fab,
  Chip,
  useMediaQuery,
  useTheme,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  SelectChangeEvent,
} from '@mui/material';
import {
  Add as AddIcon,
  Book as BookIcon,
  Lock as LockIcon,
  Label as LabelIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { usePasskey } from '../contexts/PasskeyContext';
import {
  getUserJournals,
  createJournalEntry,
  getJournalLabels,
  JournalEntry,
} from '../services/journalService';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';

const Journals: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { currentUser } = useAuth();
  const { passkey } = usePasskey();
  const navigate = useNavigate();

  const [journals, setJournals] = useState<JournalEntry[]>([]);
  const [labels, setLabels] = useState<string[]>([]);
  const [selectedLabel, setSelectedLabel] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const loadJournals = useCallback(async () => {
    if (!currentUser || !passkey) return;

    try {
      setLoading(true);
      const filterLabel = selectedLabel === 'all' ? null : selectedLabel;
      const fetchedJournals = await getUserJournals(currentUser.uid, passkey, filterLabel);
      setJournals(fetchedJournals);
    } catch (error) {
      console.error('Error loading journals:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser, passkey, selectedLabel]);

  const loadLabels = useCallback(async () => {
    if (!currentUser) return;
    
    try {
      const fetchedLabels = await getJournalLabels(currentUser.uid);
      setLabels(fetchedLabels);
    } catch (error) {
      console.error('Error loading labels:', error);
    }
  }, [currentUser]);

  useEffect(() => {
    loadJournals();
    loadLabels();
  }, [loadJournals, loadLabels]);

  const handleLabelChange = (event: SelectChangeEvent<string>) => {
    setSelectedLabel(event.target.value);
  };

  const handleCreateJournal = async () => {
    if (!currentUser || !passkey) return;

    try {
      const journalId = await createJournalEntry(
        currentUser.uid,
        { title: 'New Journal', content: '', labels: [] },
        passkey
      );
      navigate(`/journal/${journalId}`);
    } catch (error) {
      console.error('Error creating journal:', error);
    }
  };

  const handleJournalClick = (journal: JournalEntry) => {
    if (!journal.isLocked) {
      navigate(`/journal/${journal.id}`);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return format(date, 'MMM d, yyyy');
  };

  const getPreview = (content: string) => {
    const stripped = content.replace(/<[^>]*>/g, '').trim();
    return stripped.length > 100 ? stripped.substring(0, 100) + '...' : stripped;
  };

  return (
    <>
      <Header onMenuClick={() => setSidebarOpen(!sidebarOpen)} showMenuButton={isMobile} />
      <Box sx={{ display: 'flex', mt: '64px' }}>
        <Sidebar
          open={sidebarOpen}
          selectedSection="journals"
          onSectionSelect={(section) => {
            if (section === 'thoughts') navigate('/');
            else if (section === 'diary') navigate('/diary');
            else if (section === 'journals') navigate('/journals');
            else if (section === 'download') navigate('/download');
          }}
          onClose={() => setSidebarOpen(false)}
        />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            width: '100%',
          }}
        >
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
                Journals
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Long-form writing and stories
              </Typography>
            </Box>
            
            {/* Labels Dropdown */}
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="journal-label-filter-label">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LabelIcon sx={{ fontSize: 18 }} />
                  Filter by Label
                </Box>
              </InputLabel>
              <Select
                labelId="journal-label-filter-label"
                id="journal-label-filter"
                value={selectedLabel}
                label="Filter by Label"
                onChange={handleLabelChange}
                sx={{
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}
              >
                <MenuItem value="all">
                  <em>All Journals</em>
                </MenuItem>
                {labels.map((label) => (
                  <MenuItem key={label} value={label}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {selectedLabel !== 'all' && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="primary">
                Showing journals labeled: <strong>{selectedLabel}</strong>
              </Typography>
            </Box>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : journals.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                px: 2,
              }}
            >
              <BookIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {selectedLabel === 'all' ? 'No journals yet' : `No journals with label "${selectedLabel}"`}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {selectedLabel === 'all'
                  ? 'Click the + button to create your first journal'
                  : 'Try selecting a different label or create a new journal'}
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {journals.map((journal) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={journal.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      opacity: journal.isLocked ? 0.7 : 1,
                      background: (theme) =>
                        theme.palette.mode === 'dark'
                          ? 'linear-gradient(145deg, #2D3748 0%, #1A202C 100%)'
                          : 'linear-gradient(145deg, #FFFEF0 0%, #FFF9E6 100%)',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <CardActionArea
                      onClick={() => handleJournalClick(journal)}
                      disabled={journal.isLocked}
                      sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                    >
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          {journal.isLocked ? (
                            <LockIcon sx={{ mr: 1, color: 'warning.main' }} />
                          ) : (
                            <BookIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
                          )}
                          <Typography
                            variant="h6"
                            component="h2"
                            sx={{
                              fontWeight: 600,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              flexGrow: 1,
                            }}
                          >
                            {journal.title}
                          </Typography>
                        </Box>

                        {!journal.isLocked && journal.content && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mb: 2,
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              fontFamily: '"Caveat", cursive',
                              fontSize: '1rem',
                            }}
                          >
                            {getPreview(journal.content)}
                          </Typography>
                        )}

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 'auto' }}>
                          {journal.labels?.slice(0, 3).map((label) => (
                            <Chip key={label} label={label} size="small" variant="outlined" />
                          ))}
                        </Box>

                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mt: 1 }}
                        >
                          {formatDate(journal.updatedAt)}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          <Fab
            color="primary"
            aria-label="add journal"
            sx={{
              position: 'fixed',
              bottom: 32,
              right: 32,
            }}
            onClick={handleCreateJournal}
          >
            <AddIcon />
          </Fab>
        </Box>
      </Box>
    </>
  );
};

export default Journals;

